/**
 * useBacklog -- Kanban backlog task store (Zustand).
 */
import { create } from 'zustand'
import {
  getBacklogTasks,
  createBacklogTask,
  updateBacklogTask,
  deleteBacklogTask,
  moveBacklogTask,
} from '@/lib/api'
import type { BacklogTask, BacklogTaskCreate, BacklogTaskUpdate } from '@/lib/types'

interface BacklogState {
  tasks: BacklogTask[]
  loading: boolean
  error: string | null

  fetchTasks: () => Promise<void>
  create: (body: BacklogTaskCreate) => Promise<void>
  update: (id: number, body: BacklogTaskUpdate) => Promise<void>
  move: (id: number, status: BacklogTask['status']) => Promise<void>
  remove: (id: number) => Promise<void>
}

export const useBacklog = create<BacklogState>()((set, get) => ({
  tasks: [],
  loading: false,
  error: null,

  fetchTasks: async () => {
    set({ loading: true, error: null })
    try {
      const tasks = await getBacklogTasks()
      set({ tasks, loading: false })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load tasks'
      set({ loading: false, error: msg })
    }
  },

  create: async (body: BacklogTaskCreate) => {
    try {
      const task = await createBacklogTask(body)
      set(s => ({ tasks: [...s.tasks, task] }))
    } catch { /* ignore — caller handles */ }
  },

  update: async (id: number, body: BacklogTaskUpdate) => {
    try {
      const updated = await updateBacklogTask(id, body)
      set(s => ({ tasks: s.tasks.map(t => t.id === id ? updated : t) }))
    } catch { /* ignore */ }
  },

  move: async (id: number, status: BacklogTask['status']) => {
    // Optimistic update
    set(s => ({
      tasks: s.tasks.map(t => t.id === id ? { ...t, status } : t),
    }))
    try {
      const updated = await moveBacklogTask(id, status)
      set(s => ({ tasks: s.tasks.map(t => t.id === id ? updated : t) }))
    } catch {
      // Revert on failure by re-fetching
      get().fetchTasks()
    }
  },

  remove: async (id: number) => {
    try {
      await deleteBacklogTask(id)
      set(s => ({ tasks: s.tasks.filter(t => t.id !== id) }))
    } catch { /* ignore */ }
  },
}))
